import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Polls the owner order notifications endpoint every `intervalMs` and
 * surfaces two things:
 *   • `pendingCount` — used to render the red badge on the Orders nav item
 *   • toast + short beep for every newly-created order since the previous
 *     poll (skips the very first poll to avoid a toast bomb on tab open)
 *
 * A single AudioContext is lazily created and reused for the beep so
 * browsers don't yell about "user gesture required" after the first user
 * interaction with the page.
 */

const STORAGE_KEY = "dialflow_owner_last_seen_orders_v1";

function useBeeper(enabled) {
  const ctxRef = useRef(null);
  return useCallback(() => {
    if (!enabled) return;
    try {
      // Lazy so autoplay policy doesn't complain on page-open.
      if (!ctxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        ctxRef.current = new AC();
      }
      const ctx = ctxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.14);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.28);
    } catch {
      /* silent — beep is best-effort */
    }
  }, [enabled]);
}

export function useOrderNotifications({
  enabled = true,
  intervalMs = 15000,
  beep = true,
  onNewOrder,
} = {}) {
  const [pendingCount, setPendingCount] = useState(0);
  const sinceRef = useRef(null);
  const firstPollRef = useRef(true);
  const doBeep = useBeeper(beep);

  // Hydrate last-seen timestamp from localStorage once so a hard reload
  // doesn't re-fire toasts for orders the owner has already acknowledged.
  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v) {
        sinceRef.current = v;
        // We have a real "since" from a prior session — safe to fire toasts on
        // the very first poll (any orders newer than v are genuinely unseen).
        firstPollRef.current = false;
      }
    } catch { /* ignore */ }
  }, []);

  const poll = useCallback(async () => {
    try {
      const params = sinceRef.current ? { since: sinceRef.current } : {};
      const { data } = await api.get("/marketplace/orders/notifications", { params });
      setPendingCount(Number(data?.pending_count || 0));

      const list = Array.isArray(data?.new_orders) ? data.new_orders : [];
      // Skip toasts on the very first poll of a fresh (no-storage) session.
      if (!firstPollRef.current && list.length > 0) {
        list
          .slice()
          .reverse() // oldest → newest so the newest ends up on top of the stack
          .forEach((o) => {
            const total = Number(o.total || 0).toLocaleString("en-IN");
            const who = (o.customer_name || o.customer_mobile || "Customer").split(" ")[0];
            toast.success(`New order · ₹${total}`, {
              description: `${who} · ${o.payment_method === "cod" ? "COD" : "Prepaid"} · #${(o.id || "").slice(0, 8)}`,
              duration: 8000,
              action: {
                label: "Open",
                onClick: () => { window.location.assign("/dashboard/orders"); },
              },
            });
          });
        doBeep();
        if (typeof onNewOrder === "function") onNewOrder(list);
      }

      // Advance the "since" pointer to the newest order the server has, so
      // the next poll only surfaces orders newer than that. If there are no
      // orders yet, use server_time so nothing older resurfaces.
      const next = data?.latest_created_at || data?.server_time;
      if (next) {
        sinceRef.current = next;
        try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
      }
      firstPollRef.current = false;
    } catch {
      /* transient network failure — silent, we'll try again next tick */
    }
  }, [doBeep, onNewOrder]);

  useEffect(() => {
    if (!enabled) return undefined;
    poll(); // fire immediately so the badge is accurate on first paint
    const id = window.setInterval(poll, intervalMs);
    // Also refresh when the tab comes back to the foreground.
    const onVisible = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, intervalMs, poll]);

  return { pendingCount, refresh: poll };
}

/** Clears the badge (used by the Orders page when the owner opens it). */
export function acknowledgeOrders() {
  try {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
  } catch { /* ignore */ }
}
